/**
 * Date formatting utilities.
 * All helpers accept UTC date strings (with or without trailing "Z")
 * and produce output in the user's local timezone.
 */

/** Parse a UTC date string, appending 'Z' if needed so the browser treats it as UTC. */
export const parseUtc = (utcStr: string): Date => {
  const trimmed = utcStr.trim();
  // If already has timezone info (Z or +/- offset), parse as-is
  if (/Z$/i.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  return new Date(trimmed + 'Z');
};

/** "May 01, 2025  •  2:30 PM" — date + time in local timezone. */
export const formatDateTime = (utcStr: string): string => {
  const d = parseUtc(utcStr);
  const datePart = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const timePart =
    d.getHours() === 0 && d.getMinutes() === 0
      ? 'Unknown'
      : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart}  •  ${timePart}`;
};

/** "2:30 PM" — time-only in local timezone. */
export const formatTime = (utcStr?: string | null): string => {
  if (!utcStr) return '';
  try {
    return parseUtc(utcStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

/** "2:30:15 PM" — time with seconds in local timezone. */
export const formatTimeWithSeconds = (utcStr?: string | null): string => {
  if (!utcStr) return '';
  try {
    return parseUtc(utcStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
};

/** "Just now" / "5m ago" / "2h ago" — relative time from now. */
export const formatRelativeTime = (utcStr: string): string => {
  try {
    const diff = Date.now() - parseUtc(utcStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ago`;
  } catch {
    return '';
  }
};
